"use client";

import Link from "next/link";
import { Star, Heart } from "lucide-react";
import { resolveProductImages } from "@/utils/media";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SafeImage } from "@/components/common/SafeImage";
import { getDisplayPrice } from "@/services/pricingService";

const formatPrice = (value) => {
  const amount = Number(value || 0);
  return priceFormatter.format(amount);
};

const priceFormatter = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 0,
});
const STAR_KEYS = [0, 1, 2, 3, 4];


export const FigmaProductCard = ({ product }) => {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { isAuthenticated } = useAuth();

  const productId = product?._id || product?.id;
  const isLiked = isInWishlist(productId);

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error("Please login to add items to your wishlist", {
        description: "Your wishlist is saved to your account for access on any device.",
        action: {
          label: "Login",
          onClick: () => window.location.href = "/login"
        }
      });
      return;
    }

    if (isLiked) {
      removeFromWishlist(productId);
    } else {
      addToWishlist(product);
    }
  };

  const { price: salePrice, originalPrice: rawOriginalPrice } = getDisplayPrice(product, null);
  const image = resolveProductImages(product?.images || [])[0] || "";
  const mrp = Number(rawOriginalPrice || product?.retailPrice?.mrp || product?.mrp || salePrice || 0);
  const hasDiscount = mrp > salePrice;
  const reviewCount = Number(
    product?.vendor?.reviewCount || product?.reviews?.length || 0,
  );
  const stockQty = Number(product?.stockQty ?? product?.vendor?.stockQty ?? 0);
  const inStock = stockQty > 0;
  const productHref = `/product/${product?.slug || product?.id || product?._id}`;

  return (
    <article className="group flex h-full min-h-76 flex-col overflow-hidden rounded-[14px] border border-zinc-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-400/70 hover:shadow-[0_12px_32px_rgba(24,24,27,0.10)]">
      <Link
        href={productHref}
        className="relative block aspect-[1.22/1] bg-white"
      >
        <div className="absolute inset-3 overflow-hidden rounded-[12px] border border-zinc-100 bg-zinc-50">
          {image ? (
            <SafeImage
              src={image}
              alt={product.name || "Product"}
              fill
              className="object-contain p-3 transition-transform duration-700 group-hover:scale-110"
              sizes="(max-width: 768px) 90vw, (max-width: 1280px) 33vw, 25vw"
            />
          ) : (
            <SafeImage
              src="/placeholder.jpg"
              alt={product.name || "Product placeholder"}
              fill
              className="object-contain p-3"
              sizes="(max-width: 768px) 90vw, (max-width: 1280px) 33vw, 25vw"
            />
          )}
        </div>
        <span className="absolute left-3 top-3 z-10 rounded-[4px] bg-zinc-950/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-amber-400 ring-1 ring-amber-500/40 shadow-sm backdrop-blur-sm">
          {product.productType || "OEM"}
        </span>
      </Link>

      <div className="flex flex-1 flex-col gap-1.5 px-3.5 pb-3.5 pt-3 sm:px-4">
        <div className="space-y-1.5">
          <div className="flex items-start justify-between gap-2">
            <Link
              href={productHref}
              className="group/title block flex-1 text-[13px] font-semibold leading-5 mt-1 text-zinc-800 transition-colors hover:text-amber-600 sm:text-sm"
            >
              <span className="line-clamp-2">{product.name || "Product"}</span>
            </Link>
            <button
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-red-50 hover:text-red-500 active:scale-95",
                isLiked ? "text-red-500 bg-red-50" : "text-zinc-400 bg-transparent"
              )}
              aria-label={isLiked ? "Remove from wishlist" : "Add to wishlist"}
              onClick={handleWishlistClick}
            >
              <Heart className={cn("h-5 w-5", isLiked && "fill-current")} />
            </button>
          </div>

          <div className="flex items-center gap-0.5 text-amber-400">
            {STAR_KEYS.map((index) => (
              <Star key={index} className="h-3 w-3 fill-amber-400" />
            ))}
            <span className="ml-1 text-[11px] font-medium text-zinc-500">
              ({reviewCount || 0})
            </span>
          </div>

          <div className="flex items-end justify-between gap-3 pt-1">
            <div className="flex items-end gap-2">
              <span className="font-display text-[19px] font-bold tracking-tight text-amber-600">
                Rs.{formatPrice(salePrice)}
              </span>
              {hasDiscount ? (
                <span className="pb-0.5 text-sm text-zinc-400 line-through">
                  Rs.{formatPrice(mrp)}
                </span>
              ) : null}
          </div>
          <span
            className={cn(
              "rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ring-1",
              inStock
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : "bg-red-50 text-red-600 ring-red-200"
            )}
          >
            {inStock ? "IN STOCK" : "OUT OF STOCK"}
          </span>
        </div>
      </div>
      </div>
    </article>
  );
};
